import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Copy, Mail, Package } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { clearCart } from "../lib/cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async (n = 0) => {
    if (n >= 10) { setStatus("timeout"); return; }
    try {
      const r = await api.get(`/checkout/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setStatus("paid");
        setOrder(r.data.order);
        clearCart();
        return;
      }
      if (r.data.status === "expired") { setStatus("expired"); return; }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(n + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) poll(0);
    else setStatus("error");
  }, [sessionId, poll]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (status === "checking" || status === "timeout") {
    return (
      <div data-testid="payment-checking" className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="inline-block surface px-6 py-3 font-mono text-sm">
          <span className="text-[#00FF66]">$</span> verifying payment{".".repeat((attempts % 3) + 1)}<span className="cursor-blink text-[#00FF66]">_</span>
        </div>
        <p className="text-[#A1A1AA] text-sm mt-4">{status === "timeout" ? "Still working on it. Check your email or order lookup in a few moments." : "Don't close this page."}</p>
      </div>
    );
  }

  if (status !== "paid" || !order) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold">Payment not completed</h1>
        <p className="text-[#A1A1AA] mt-2">If you were charged, please check your email or use order lookup.</p>
        <Link to="/order-lookup" className="btn-primary mt-6 inline-flex">Order lookup</Link>
      </div>
    );
  }

  return (
    <div data-testid="payment-success-page" className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 glow-primary">
          <Check className="w-8 h-8 text-[#00FF66]" />
        </div>
        <h1 className="font-heading font-bold text-4xl mt-6">Payment confirmed</h1>
        <p className="text-[#A1A1AA] mt-2">Order <span className="font-mono text-[#00FF66]">{order.id}</span> · ${order.total.toFixed(2)}</p>
      </div>

      <div className="surface p-6 md:p-8">
        <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66] flex items-center gap-2"><Package className="w-3.5 h-3.5" />// your delivery</div>

        {/* Account-email items (Autodesk subscriptions) */}
        {order.items?.some((it) => it.delivery_type === "account_email") && (
          <div className="mt-4 space-y-3">
            {order.items.filter((it) => it.delivery_type === "account_email").map((it, i) => (
              <div key={`ae-${i}`} className="border border-[#1F8FE8]/40 rounded-md p-4 bg-[#1F8FE8]/5">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading font-semibold">{it.name}</div>
                  <span className="chip-mono !bg-[#F59E0B]/10 !text-[#F59E0B] !border-[#F59E0B]/30">activating</span>
                </div>
                <div className="text-xs text-[#A1A1AA] mt-1 font-mono">{it.subscription_period || "1 year"} subscription · activating on <span className="text-white">{it.account_email}</span></div>
                <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
                  Our team is activating this subscription on your Autodesk account. Typically completed within a few minutes — at most a few hours during off-peak. You'll get a confirmation email once it's live.
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Key items */}
        {order.license_keys?.length > 0 && (
          <div className="mt-4 space-y-3">
            {order.license_keys.map((k, i) => (
              <div key={i} data-testid={`license-key-${i}`} className="border border-[#1E293B] rounded-md p-4 bg-[#0a0a0f]">
                <div className="text-xs text-[#A1A1AA] font-mono">{k.product_name}</div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <code className="font-mono text-[#00FF66] text-base break-all">{k.key}</code>
                  <button onClick={() => copy(k.key)} className="btn-ghost shrink-0" data-testid={`copy-key-${i}`}>
                    <Copy className="w-3.5 h-3.5" /> copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Nothing yet */}
        {!order.license_keys?.length && !order.items?.some((it) => it.delivery_type === "account_email") && (
          <div className="mt-4 surface p-5 border-[#F59E0B]/40">
            <div className="text-[#F59E0B] font-mono text-sm">⏳ keys are being prepared</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Stock is being assigned manually. You'll receive an email at <span className="font-mono text-white">{order.email}</span> within a few minutes.</p>
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-[#A1A1AA] font-mono">
          <Mail className="w-3.5 h-3.5 text-[#00FF66]" />
          A copy was emailed to {order.email}
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-3">
        <Link to="/products" className="btn-ghost">continue shopping</Link>
        <Link to="/order-lookup" className="btn-primary">manage orders</Link>
      </div>
    </div>
  );
}
